import { z } from 'zod';
import { issuerSchema } from '../auth/issuers';
import {
  EXTRACTION_MAX_BYTES,
  pageRangeSchema,
  PageRange,
  textMaterialKeySchema,
} from '../generation/studyMaterial';
import { domainSchema } from '../source/source';
import {
  AGE_GROUPS,
  type AgeGroup,
  ageGroupsSchema,
  type Language,
  LANGUAGES,
  languageSchema,
  tagsSchema,
  targetLanguagesSchema,
  taskIdSchema,
  textSchema,
  timestampSchema,
} from '../task/task';
import {
  DEFAULT_STEP_MODELS,
  type JobStepModels,
  jobStepModelsSchema,
  resolveStepModels,
  type StepModelChoices,
} from './stepModels';

/**
 * How a job's tasks are sourced: from a written request, or from a PDF's or photo set's text
 * (PDG-D2, QZ-D80, QZ-D91).
 */
export const JOB_METHODS = ['prompt', 'pdf', 'photos'] as const;

/** A job's method, validated against `JOB_METHODS` (PDG-D2). */
export const jobMethodSchema = z.enum(JOB_METHODS);
export type JobMethod = z.infer<typeof jobMethodSchema>;

/**
 * Whether a job is built from material text, a PDF's or a photo set's, rather than a prompt
 * (QZ-D91).
 */
export const isMaterialMethod = (method: JobMethod): method is 'pdf' | 'photos' =>
  method !== 'prompt';

/** A job's lifecycle status (QZ-D56, QZ-D57). */
export const JOB_STATUSES = ['planning', 'running', 'done', 'failed', 'cancelled'] as const;

/** A chunk's lifecycle status (QZ-D56). */
export const CHUNK_STATUSES = ['queued', 'running', 'done', 'failed'] as const;

/** The steps of one chunk's generation (QZ-D51). */
export const GENERATION_STEPS = ['plan', 'write', 'check'] as const;

/**
 * Why the check step dropped a written task (QZ-D51); `unchecked` is a task the check gave no
 * result for, even when asked again (QZ-D157).
 */
export const DROP_REASONS = ['duplicate', 'failedQa', 'invalid', 'unchecked'] as const;

/** The number of tasks one chunk writes at a time (QZ-D49). */
export const CHUNK_SIZE = 10;

/** The most attempts a step or chunk gets before it is given up on (QZ-D56). */
export const MAX_ATTEMPTS = 3;

/** The most characters a job's request may hold (QZ-D48). */
export const JOB_REQUEST_MAX = 2000;

/** The most tasks a job may be asked to generate (QZ-D48). */
export const JOB_TASK_COUNT_MAX = 200;

/**
 * The sums a plan's task counts may reach for a job asked for `taskCount` tasks (QZ-D162):
 * 90% rounded up to 115% rounded down. A prompt job's plan may land anywhere in it; a PDF or
 * photo job's may also go below `min`, with a reason (QZ-D73).
 */
export const plannedTaskCountRange = (taskCount: number): { min: number; max: number } => ({
  // Integer percentages: `200 * 1.15` is 229.99…, which would floor to 229.
  min: Math.ceil((taskCount * 90) / 100),
  max: Math.floor((taskCount * 115) / 100),
});

/** The most characters a request file's text may hold (RQF-D1). */
export const REQUEST_FILE_MAX_CHARS = 100_000;

/** The most characters a request file's name may hold (RQF-D1). */
export const REQUEST_FILE_NAME_MAX = 200;

/** A request file's possible problems, checked in this order by `requestFileTextProblem` (RQF-D1). */
export type RequestFileTextProblem = 'empty' | 'notText' | 'tooLong';

/**
 * The problem with a request file's text, or `undefined` when it has none: empty, holding a NUL
 * byte (so not UTF-8 text), or over `REQUEST_FILE_MAX_CHARS` (RQF-D1). The one rule, used by the
 * schema and by New Job.
 */
export const requestFileTextProblem = (text: string): RequestFileTextProblem | undefined => {
  if (text.trim() === '') return 'empty';
  if (text.includes('\u0000')) return 'notText';
  if (text.length > REQUEST_FILE_MAX_CHARS) return 'tooLong';
  return undefined;
};

/**
 * The write models jobs stored before QZ-D170 may name; kept so they still read.
 */
export const WRITE_MODELS = ['claude-opus-5-5', 'claude-opus-5', 'claude-sonnet-5'] as const;

/** A job's write model, validated against `WRITE_MODELS`. */
export const writeModelSchema = z.enum(WRITE_MODELS);
export type WriteModel = z.infer<typeof writeModelSchema>;

/** The write model of a job that names none, as every job stored before the choice existed. */
export const DEFAULT_WRITE_MODEL: WriteModel = WRITE_MODELS[0];

/** A job's lifecycle status, validated against `JOB_STATUSES` (QZ-D56, QZ-D57). */
export const jobStatusSchema = z.enum(JOB_STATUSES);
export type JobStatus = z.infer<typeof jobStatusSchema>;

/** A job's kind: an admin's job, or a GPS Quiz user's job started over `/consumer/jobs` (EXJ-D1). */
export const JOB_KINDS = ['job', 'userJob'] as const;

/** A job's kind, validated against `JOB_KINDS` (EXJ-D1). */
export const jobKindSchema = z.enum(JOB_KINDS);
export type JobKind = z.infer<typeof jobKindSchema>;

/**
 * A job's approval (QZ-D192): `manual` stores every task as a draft; `passed` approves a task the
 * check passed unchanged, the rest stay drafts; `always` approves every task the job stores,
 * whatever its check outcome.
 */
export const JOB_APPROVALS = ['manual', 'passed', 'always'] as const;

/** A job's approval, validated against `JOB_APPROVALS` (QZ-D192). */
export const jobApprovalSchema = z.enum(JOB_APPROVALS);
export type JobApproval = z.infer<typeof jobApprovalSchema>;

/**
 * A job configuration's id, typed by the admin and sent by a consumer's job request, so it
 * cannot be renamed (QZ-D194): at most 64 characters, lowercase letters and digits, hyphen
 * separated. Lives here, not in `jobConfig.ts`, so `jobConfig.ts` imports `job.ts` and never the
 * other way.
 */
export const jobConfigIdSchema = z
  .string()
  .max(64)
  .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/);

/** A user job's owner, from the caller's token (QZ-D182, QZ-D197). */
export const jobOwnerSchema = z
  .object({
    issuer: issuerSchema,
    userId: z.string().min(1).max(128),
    username: z.string().max(128),
  })
  .strip();
export type JobOwner = z.infer<typeof jobOwnerSchema>;

/** A chunk's lifecycle status, validated against `CHUNK_STATUSES` (QZ-D56). */
export const chunkStatusSchema = z.enum(CHUNK_STATUSES);
export type ChunkStatus = z.infer<typeof chunkStatusSchema>;

/** One of a chunk's generation steps, validated against `GENERATION_STEPS` (QZ-D51). */
export const generationStepSchema = z.enum(GENERATION_STEPS);
export type GenerationStep = z.infer<typeof generationStepSchema>;

/** Why a written task was dropped, validated against `DROP_REASONS` (QZ-D51). */
export const dropReasonSchema = z.enum(DROP_REASONS);
export type DropReason = z.infer<typeof dropReasonSchema>;

/** Micro-dollars: an integer number of millionths of a US dollar (QZ-D55). */
export const microUsdSchema = z.number().int().nonnegative();

/** One generation step's accumulated cost, and the model that last ran it (QZ-D55). */
export const stepCostSchema = z
  .object({
    model: z.string().min(1).optional(),
    inputTokens: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
    webSearches: z.number().int().nonnegative(),
    microUsd: microUsdSchema,
  })
  .strip();
export type StepCost = z.infer<typeof stepCostSchema>;

/** A chunk's written tasks that did not survive the check, by reason (QZ-D51). */
export const dropCountsSchema = z
  .object({
    duplicate: z.number().int().nonnegative(),
    failedQa: z.number().int().nonnegative(),
    invalid: z.number().int().nonnegative(),
    /** Absent on a chunk stored before QZ-D157, which counted these as `failedQa`. */
    unchecked: z.number().int().nonnegative().default(0),
  })
  .strip();
export type DropCounts = z.infer<typeof dropCountsSchema>;

/** One of a job's plan sub-topics, with its progress (QZ-D49, QZ-D56). */
export const chunkSchema = z
  .object({
    subTopic: textSchema.max(200),
    tags: tagsSchema,
    taskCount: z.number().int().min(1).max(CHUNK_SIZE),
    status: chunkStatusSchema,
    attempts: z.number().int().min(0).max(MAX_ATTEMPTS),
    /** The chunk's tasks stored as drafts; with `approved`, every task it stored. */
    drafted: z.number().int().nonnegative(),
    /** The chunk's tasks auto-approved (QZ-D161); absent on chunks stored before, read as 0. */
    approved: z.number().int().nonnegative().default(0),
    dropped: dropCountsSchema,
    microUsd: microUsdSchema,
    error: z.string().max(500).optional(),
  })
  .strip();
export type Chunk = z.infer<typeof chunkSchema>;

/**
 * A PDF or photo job's study material, as stored on the job: the text of its page range, never
 * the PDF or photos themselves (PDG-D2, QZ-D80, PTX-D2). `name` is the **document**, typed on
 * New Job (QZ-D100): the PDF's file name or a photo set's name, prefilled for a PDF; `sizeBytes`
 * is the stored text's size. `section` is the optional part of the document the job names
 * (QZ-D101), stored and copied to every draft, but never sent to Claude.
 */
export const studyMaterialSchema = z
  .object({
    key: textMaterialKeySchema,
    name: textSchema.max(200),
    sizeBytes: z.number().int().positive().max(EXTRACTION_MAX_BYTES),
    section: textSchema.max(200).optional(),
  })
  .strip();
export type StudyMaterial = z.infer<typeof studyMaterialSchema>;

/**
 * A prompt job's request file, as stored on the job: the key its text was written to, its
 * original name, and its character count (RQF-D2).
 */
export const requestFileSchema = z
  .object({
    key: textMaterialKeySchema,
    name: z.string().min(1).max(REQUEST_FILE_NAME_MAX),
    chars: z.number().int().min(1).max(REQUEST_FILE_MAX_CHARS),
  })
  .strict();
export type RequestFile = z.infer<typeof requestFileSchema>;

/** The JSON stored at a request file's key, `{ text }` (RQF-D2). */
export const requestFileTextSchema = z.object({ text: z.string() }).strict();
export type RequestFileText = z.infer<typeof requestFileTextSchema>;

/** A prompt job's request text, shared by `createJobRequestSchema` and `jobSchema` (QZ-D48). */
export const jobRequestSchema = textSchema.max(JOB_REQUEST_MAX);

/** A prompt job's task count, shared by `createJobRequestSchema` and `jobSchema` (QZ-D48). */
export const jobTaskCountSchema = z.number().int().min(1).max(JOB_TASK_COUNT_MAX);

/** A generation job, exactly as stored in the jobs table (QZ-D55, PDG-D2). */
export const jobSchema = z
  .object({
    jobId: taskIdSchema,
    kind: jobKindSchema,
    /** A user job's owner; absent on a job an admin starts on New Job (QZ-D197). */
    owner: jobOwnerSchema.optional(),
    /** The job configuration a user job's request named (QZ-D194). */
    jobConfigId: jobConfigIdSchema.optional(),
    method: jobMethodSchema,
    status: jobStatusSchema,
    request: jobRequestSchema.optional(),
    /** A PDF job's extra instructions for the write step, same length as a request (QZ-D68). */
    instructions: jobRequestSchema.optional(),
    studyMaterial: studyMaterialSchema.optional(),
    /** A prompt job's one request file, read as data the request refers to (QZ-D141). */
    requestFile: requestFileSchema.optional(),
    language: languageSchema,
    /** Absent on jobs stored before QZ-D130; `newJob` always writes it (TMA-D2). */
    targetLanguages: targetLanguagesSchema.optional(),
    /** Absent on jobs stored before QZ-D131; `newJob` always writes it (TMA-D2). */
    ageGroups: ageGroupsSchema.optional(),
    taskCount: jobTaskCountSchema,
    /** The sites a prompt job researches on; none means the open web (`isOpenWebJob`). */
    sourceSites: z.array(domainSchema),
    /** Absent on jobs stored before the choice existed; read as `DEFAULT_WRITE_MODEL`. */
    writeModel: writeModelSchema.optional(),
    /** A job's approval; absent on a job stored before QZ-D192, read through `jobApproval` (QZ-D192). */
    approval: jobApprovalSchema.optional(),
    /**
     * Whether a task the check passed unchanged is stored approved instead of as a draft
     * (QZ-D161). Absent on jobs stored before the choice existed, which read as off. Read only
     * through `jobApproval`, for jobs stored before QZ-D192.
     */
    autoApprove: z.boolean().optional(),
    /** Absent on jobs stored before it existed; read through `jobStepModels` (QZ-D174). */
    stepModels: jobStepModelsSchema.optional(),
    /** What translating the job's tasks has cost, added per call (QZ-D175). */
    translationMicroUsd: z.number().nonnegative().optional(),
    title: textSchema.max(200).optional(),
    planAttempts: z.number().int().min(0).max(MAX_ATTEMPTS),
    chunks: z.array(chunkSchema),
    cost: z.object({ plan: stepCostSchema, write: stepCostSchema, check: stepCostSchema }).strip(),
    /** A PDF job's page range into its material, set at creation (PTX-D5). */
    pageRange: pageRangeSchema.optional(),
    /** The PDF's total page count, set at creation (PTX-D5). */
    pdfPageCount: z.number().int().min(1).optional(),
    /** The character count of the job's page range, set at creation (PTX-D5). */
    textChars: z.number().int().min(0).optional(),
    /** A PDF job's plan note, once planned (PDG-D2). */
    planNote: textSchema.max(500).optional(),
    error: z.string().max(500).optional(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
  })
  .strip()
  .superRefine((job, ctx) => {
    if (job.targetLanguages !== undefined) {
      if (new Set(job.targetLanguages).size !== job.targetLanguages.length) {
        ctx.addIssue({
          code: 'custom',
          path: ['targetLanguages'],
          message: 'A language may be picked only once.',
        });
      }
      if (!job.targetLanguages.includes(job.language)) {
        ctx.addIssue({
          code: 'custom',
          path: ['targetLanguages'],
          message: 'The target languages must include the job language.',
        });
      }
    }
    if (job.ageGroups !== undefined && new Set(job.ageGroups).size !== job.ageGroups.length) {
      ctx.addIssue({
        code: 'custom',
        path: ['ageGroups'],
        message: 'An age group may be picked only once.',
      });
    }

    if (job.method === 'prompt') {
      if (job.request === undefined) {
        ctx.addIssue({
          code: 'custom',
          path: ['request'],
          message: 'A prompt job needs a request.',
        });
      }
      if (job.studyMaterial !== undefined) {
        ctx.addIssue({
          code: 'custom',
          path: ['studyMaterial'],
          message: 'A prompt job must not have a study material.',
        });
      }
      if (job.instructions !== undefined) {
        ctx.addIssue({
          code: 'custom',
          path: ['instructions'],
          message: 'A prompt job must not have instructions.',
        });
      }
      if (job.pageRange !== undefined) {
        ctx.addIssue({
          code: 'custom',
          path: ['pageRange'],
          message: 'A prompt job must not have a page range.',
        });
      }
      if (job.pdfPageCount !== undefined) {
        ctx.addIssue({
          code: 'custom',
          path: ['pdfPageCount'],
          message: 'A prompt job must not have a PDF page count.',
        });
      }
      if (job.textChars !== undefined) {
        ctx.addIssue({
          code: 'custom',
          path: ['textChars'],
          message: 'A prompt job must not have a text character count.',
        });
      }
      if (job.chunks.length > 0) {
        // QZ-D162: within -10% to +15% of taskCount.
        const sum = job.chunks.reduce((total, chunk) => total + chunk.taskCount, 0);
        const range = plannedTaskCountRange(job.taskCount);
        if (sum < range.min || sum > range.max) {
          ctx.addIssue({
            code: 'custom',
            path: ['chunks'],
            message:
              "The chunks' task counts must sum to within -10% to +15% of the job's task count.",
          });
        }
      }
    } else if (isMaterialMethod(job.method)) {
      if (job.studyMaterial === undefined) {
        ctx.addIssue({
          code: 'custom',
          path: ['studyMaterial'],
          message: 'A PDF or photo job needs a study material.',
        });
      }
      if (job.request !== undefined) {
        ctx.addIssue({
          code: 'custom',
          path: ['request'],
          message: 'A PDF or photo job must not have a request.',
        });
      }
      if (job.requestFile !== undefined) {
        ctx.addIssue({
          code: 'custom',
          path: ['requestFile'],
          message: 'Only a prompt job may have a request file.',
        });
      }
      if (job.pageRange === undefined) {
        ctx.addIssue({
          code: 'custom',
          path: ['pageRange'],
          message: 'A PDF or photo job needs a page range.',
        });
      }
      if (job.pdfPageCount === undefined) {
        ctx.addIssue({
          code: 'custom',
          path: ['pdfPageCount'],
          message: 'A PDF or photo job needs a PDF page count.',
        });
      }
      if (job.textChars === undefined) {
        ctx.addIssue({
          code: 'custom',
          path: ['textChars'],
          message: 'A PDF or photo job needs a text character count.',
        });
      }
      if (job.chunks.length > 0) {
        // QZ-D73: a PDF or photo job's chunks may undershoot taskCount, once a chunk is dropped
        // as a dup; QZ-D162: and may overshoot it by up to 15%.
        const sum = job.chunks.reduce((total, chunk) => total + chunk.taskCount, 0);
        if (sum > plannedTaskCountRange(job.taskCount).max) {
          ctx.addIssue({
            code: 'custom',
            path: ['chunks'],
            message:
              "The chunks' task counts must not exceed the job's task count by more than 15%.",
          });
        }
      }
      if (
        job.pageRange !== undefined &&
        job.pdfPageCount !== undefined &&
        job.pageRange.to > job.pdfPageCount
      ) {
        ctx.addIssue({
          code: 'custom',
          path: ['pageRange', 'to'],
          message: 'A page range must not reach past pdfPageCount.',
        });
      }
    }
  });
export type Job = z.infer<typeof jobSchema>;

/**
 * A prompt job with no source sites researches the whole web and its tasks cite no source. A
 * PDF job also has no sites, but uses no web tools at all, so it is never an open-web job
 * (QZ-D78).
 */
export const isOpenWebJob = (job: Pick<Job, 'method' | 'sourceSites'>): boolean =>
  job.method === 'prompt' && job.sourceSites.length === 0;

/**
 * The job's approval (QZ-D192): `job.approval` when set, else `passed` for a job whose
 * `autoApprove` was `true`, else `manual`. The only reader of either field.
 */
export const jobApproval = (job: Pick<Job, 'approval' | 'autoApprove'>): JobApproval =>
  job.approval ?? (job.autoApprove === true ? 'passed' : 'manual');

/** Whether a job is a GPS Quiz user's job, started over `/consumer/jobs` (EXJ-D1). */
export const isUserJob = (job: Pick<Job, 'kind'>): boolean => job.kind === 'userJob';

/** The job's total cost: the sum of its steps and its translations, never the chunks' (QZ-D55, QZ-D175). */
export const jobMicroUsd = (job: Job): number =>
  job.cost.plan.microUsd +
  job.cost.write.microUsd +
  job.cost.check.microUsd +
  (job.translationMicroUsd ?? 0);

/**
 * The job's target languages, or a copy of `LANGUAGES` when absent (TMA-D2). The only reader of
 * `Job.targetLanguages`.
 */
export const jobTargetLanguages = (job: Pick<Job, 'targetLanguages'>): Language[] =>
  job.targetLanguages ? [...job.targetLanguages] : [...LANGUAGES];

/**
 * The job's step models, or for a job stored without them, what it ran with: plan and check on
 * Sonnet 5 at medium and high, write on its write model at high (QZ-D174). The only reader of
 * `stepModels`.
 */
export const jobStepModels = (job: Pick<Job, 'stepModels' | 'writeModel'>): JobStepModels => {
  if (job.stepModels) return job.stepModels;
  const model = job.writeModel ?? DEFAULT_WRITE_MODEL;
  const family = model === 'claude-sonnet-5' ? 'sonnet' : 'opus';
  return {
    plan: { family: 'sonnet', effort: 'medium', model: 'claude-sonnet-5' },
    write: { family, effort: 'high', model },
    check: { family: 'sonnet', effort: 'high', model: 'claude-sonnet-5' },
  };
};

/**
 * The job's age groups, or a copy of `AGE_GROUPS` when absent (TMA-D2). The only reader of
 * `Job.ageGroups`.
 */
export const jobAgeGroups = (job: Pick<Job, 'ageGroups'>): AgeGroup[] =>
  job.ageGroups ? [...job.ageGroups] : [...AGE_GROUPS];

/**
 * `newJob`'s input (PDG-D2). An exported interface, not a `Pick<Job, ...>`, because `jobApi.ts`
 * imports `job.ts`, so the reverse import would be a cycle. `CreateJobRequest` is assignable to it.
 */
export interface NewJobInput {
  method?: JobMethod;
  request?: string;
  instructions?: string;
  language: Language;
  targetLanguages?: Language[];
  ageGroups?: AgeGroup[];
  taskCount: number;
  stepModels?: StepModelChoices;
  /** Omitted means `'manual'` (QZ-D192). */
  approval?: JobApproval;
  /** Omitted means `'job'`, an admin's job (EXJ-D1). */
  kind?: JobKind;
  /** A user job's owner (QZ-D197). */
  owner?: JobOwner;
  /** The job configuration a user job's request named (QZ-D194). */
  jobConfigId?: string;
  materialName?: string;
  section?: string;
}

/**
 * `newJob`'s optional fifth argument: a PDF or photo job's own text material, already written
 * to `textMaterial.key` by the caller (PTX-D2, PTX-D5).
 */
export interface NewJobTextMaterial {
  key: string;
  sizeBytes: number;
  pdfPageCount: number;
  pageRange: PageRange;
  textChars: number;
}

/**
 * A new job as `POST /jobs` stores it: planning, no chunks, zero cost (QZ-D48, QZ-D55, PDG-D2).
 * A PDF or photo job takes its study material, `pageRange`, `pdfPageCount` and `textChars` from
 * `textMaterial` (PTX-D5, QZ-D91); a caller omitting it, or `input.materialName`, is a caller
 * bug. `textMaterial` is ignored on a prompt job. A prompt job may also take `requestFile`,
 * whose text the caller has already written to `requestFile.key` (QZ-D141, RQF-D2); PDF and
 * photo jobs ignore it, as a prompt job ignores `textMaterial`.
 */
export const newJob = (
  input: NewJobInput,
  sourceSites: string[],
  jobId: string,
  now: string,
  textMaterial?: NewJobTextMaterial,
  requestFile?: RequestFile
): Job => {
  const zeroCost = (): StepCost => ({
    inputTokens: 0,
    outputTokens: 0,
    webSearches: 0,
    microUsd: 0,
  });
  const base = {
    jobId,
    kind: input.kind ?? 'job',
    status: 'planning' as const,
    language: input.language,
    targetLanguages: input.targetLanguages ? [...input.targetLanguages] : [...LANGUAGES],
    ageGroups: input.ageGroups ? [...input.ageGroups] : [...AGE_GROUPS],
    taskCount: input.taskCount,
    sourceSites: [...sourceSites],
    stepModels: resolveStepModels(input.stepModels ?? DEFAULT_STEP_MODELS),
    approval: input.approval ?? 'manual',
    planAttempts: 0,
    chunks: [],
    cost: { plan: zeroCost(), write: zeroCost(), check: zeroCost() },
    createdAt: now,
    updatedAt: now,
    ...(input.owner !== undefined ? { owner: input.owner } : {}),
    ...(input.jobConfigId !== undefined ? { jobConfigId: input.jobConfigId } : {}),
  };

  if (input.method !== undefined && isMaterialMethod(input.method)) {
    if (textMaterial === undefined || input.materialName === undefined) {
      throw new Error('A PDF or photo job needs textMaterial and materialName.');
    }
    return {
      ...base,
      method: input.method,
      ...(input.instructions !== undefined ? { instructions: input.instructions } : {}),
      studyMaterial: {
        key: textMaterial.key,
        name: input.materialName,
        sizeBytes: textMaterial.sizeBytes,
        ...(input.section !== undefined ? { section: input.section } : {}),
      },
      pageRange: textMaterial.pageRange,
      pdfPageCount: textMaterial.pdfPageCount,
      textChars: textMaterial.textChars,
    };
  }

  return {
    ...base,
    method: 'prompt',
    ...(input.request !== undefined ? { request: input.request } : {}),
    ...(requestFile !== undefined ? { requestFile: { ...requestFile } } : {}),
  };
};

/** A job's statuses that end its lifecycle; none of them runs again (QZ-D106). */
export const JOB_ENDED_STATUSES = [
  'done',
  'failed',
  'cancelled',
] as const satisfies readonly JobStatus[];

/** Whether a job has ended, and so may be deleted (QZ-D106). */
export const isJobEnded = (job: Pick<Job, 'status'>): boolean =>
  (JOB_ENDED_STATUSES as readonly JobStatus[]).includes(job.status);

/**
 * Whether an ended job may be deleted (QZ-D106). A cancelled job may still have a chunk
 * finishing the step that was in flight when it was cancelled (QZ-D57); deletion waits for it.
 */
export const isJobDeletable = (job: Pick<Job, 'status' | 'chunks'>): boolean =>
  isJobEnded(job) && job.chunks.every((chunk) => chunk.status !== 'running');
